// src/components/client/StylistPolicies.tsx
// Client-facing Policies tab — mirrors mobile PoliciesTab data shape & copy.
import React, { useState, useEffect } from 'react';
import api from '../../api/apiClient';
import {
  clientFacingCancellationFeeLabel,
  clientFacingCancellationRuleDescription,
  windowUpperHoursFor,
} from '../../utils/cancellationConfirmCopy';

interface DownPaymentPolicy {
  enabled: boolean;
  percentage: number;
  acknowledgedAt: string;
}

interface LoyaltyPolicy {
  enabled: boolean;
  pointsPerDollar: number;
  acknowledgedAt: string;
}

interface CancellationRule {
  hoursBeforeAppointment: number;
  refundPercentage: number;
  cancellationFeePercentage: number;
  description: string;
}

interface CancellationPolicy {
  name?: string;
  description?: string;
  rules: CancellationRule[];
  acknowledgedAt: string;
}

export interface StylistPoliciesResponse {
  downPayment?: DownPaymentPolicy;
  loyalty?: LoyaltyPolicy;
  cancellation?: CancellationPolicy;
}

type PolicyKey = 'downPayment' | 'loyalty' | 'cancellation';

interface StylistPoliciesProps {
  stylistId: string;
}

export function hasAcknowledgedPolicies(data: StylistPoliciesResponse | null | undefined): boolean {
  if (!data) return false;
  return !!(data.downPayment || data.loyalty || data.cancellation);
}

export async function fetchStylistPolicies(stylistId: string): Promise<StylistPoliciesResponse> {
  const response = await api.get(`/profile/stylists/${stylistId}/policies`);
  return (response.data?.data || {}) as StylistPoliciesResponse;
}

const StylistPolicies: React.FC<StylistPoliciesProps> = ({ stylistId }) => {
  const [policies, setPolicies] = useState<StylistPoliciesResponse>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<PolicyKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setExpanded(null);
    setError(null);

    fetchStylistPolicies(stylistId)
      .then((data) => {
        if (cancelled) return;
        setPolicies(data);
      })
      .catch((err) => {
        console.error('Error fetching stylist policies:', err);
        if (cancelled) return;
        setPolicies({});
        setError('Failed to load policies');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stylistId]);

  const rows: { key: PolicyKey; title: string; subtitle: string }[] = [];
  if (policies.downPayment) {
    rows.push({
      key: 'downPayment',
      title: 'Booking Guarantee',
      subtitle: policies.downPayment.enabled
        ? `${policies.downPayment.percentage}% quoted (charged only if cancel late / no-show)`
        : 'No booking guarantee required',
    });
  }
  if (policies.loyalty) {
    rows.push({
      key: 'loyalty',
      title: 'Loyalty Program',
      subtitle: policies.loyalty.enabled
        ? `${policies.loyalty.pointsPerDollar} points per $1 spent`
        : 'Loyalty rewards not offered',
    });
  }
  if (policies.cancellation) {
    rows.push({
      key: 'cancellation',
      title: 'Cancellation Policy',
      subtitle: policies.cancellation.name || 'View cancellation fee rules',
    });
  }

  if (loading) {
    return (
      <div className="stylist-policies stylist-policies--loading">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="stylist-policies stylist-policies--empty">
        <p>{error}</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="stylist-policies stylist-policies--empty">
        <p>No policies available</p>
      </div>
    );
  }

  const toggle = (key: PolicyKey) => {
    setExpanded((prev) => (prev === key ? null : key));
  };

  return (
    <div className="stylist-policies">
      <p className="stylist-policies__disclaimer">
        These are the stylist&apos;s own terms. LookBel does not set or guarantee them.
      </p>

      <div className="stylist-policies__list">
        {rows.map((row) => {
          const isOpen = expanded === row.key;
          return (
            <div key={row.key} className="stylist-policies__card">
              <button
                type="button"
                className="stylist-policies__row"
                onClick={() => toggle(row.key)}
                aria-expanded={isOpen}
              >
                <span className="stylist-policies__row-text">
                  <span className="stylist-policies__row-title">{row.title}</span>
                  <span className="stylist-policies__row-subtitle">{row.subtitle}</span>
                </span>
                <span className="stylist-policies__chevron" aria-hidden="true">
                  {isOpen ? '▴' : '▾'}
                </span>
              </button>

              {isOpen && row.key === 'downPayment' && policies.downPayment && (
                <div className="stylist-policies__detail">
                  <p>
                    Status: {policies.downPayment.enabled ? 'Required' : 'Not required'}
                  </p>
                  {policies.downPayment.enabled && (
                    <p>
                      Amount: {policies.downPayment.percentage}% of the service price may be charged if you cancel late or no-show. The full amount is charged when the appointment is completed.
                    </p>
                  )}
                </div>
              )}

              {isOpen && row.key === 'loyalty' && policies.loyalty && (
                <div className="stylist-policies__detail">
                  <p>Status: {policies.loyalty.enabled ? 'Active' : 'Not offered'}</p>
                  {policies.loyalty.enabled && (
                    <p>
                      Earn {policies.loyalty.pointsPerDollar} points for every $1 you spend with
                      this stylist. 100 points = $1 off (up to 25% of a service).
                    </p>
                  )}
                </div>
              )}

              {isOpen && row.key === 'cancellation' && policies.cancellation && (
                <div className="stylist-policies__detail">
                  {policies.cancellation.description ? (
                    <p>{policies.cancellation.description}</p>
                  ) : null}
                  {(policies.cancellation.rules || []).map((rule, index, all) => (
                    <div
                      key={`${rule.hoursBeforeAppointment}-${index}`}
                      className="stylist-policies__rule"
                    >
                      <div className="stylist-policies__rule-hours">
                        {rule.hoursBeforeAppointment}+ hrs
                      </div>
                      <div className="stylist-policies__rule-refund">
                        {clientFacingCancellationFeeLabel(rule)}
                      </div>
                      <div className="stylist-policies__rule-desc">
                        {clientFacingCancellationRuleDescription({
                          ...rule,
                          windowUpperHours: windowUpperHoursFor(
                            rule.hoursBeforeAppointment,
                            all.map((r) => r.hoursBeforeAppointment)
                          ),
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StylistPolicies;
